/**
 * Reading a layer's features, whichever service backs it.
 *
 * Lifted out of QueryPanels so Select By Location does not become a third copy
 * of the same three branches — the app already carries two (this one and the
 * box-select path in App.tsx), and a third would be the one that forgets to
 * apply a layer's filter.
 *
 * A layer's own filter is always sent. A tool must not find what the map is
 * not showing: selecting a feature that is filtered out would highlight
 * nothing and tick a row the table does not have.
 */

import type { ToolContext } from './types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../../utils/arcgis';
import type { Bbox } from '../../../utils/spatialRelate';

export interface LoadOptions {
  /** Largest number of features to bring back. */
  limit?: number;
  /**
   * Only features near this box, in WGS84.
   *
   * A hint, not a contract: it is honoured where it can be expressed safely
   * and ignored otherwise, and callers must still test what comes back. Its
   * purpose is to stop a 40,000-feature service being downloaded whole when
   * the question concerns one ward.
   */
  bbox?: Bbox | null;
}

/**
 * ArcGIS understands an envelope directly on `query`.
 *
 * Unambiguous in a way the WFS equivalent is not: `inSR` states the coordinate
 * system, and an envelope has no axis-order question to get wrong.
 */
function arcgisEnvelope(bbox: Bbox): Record<string, string> {
  return {
    geometry: JSON.stringify({
      xmin: bbox.minX,
      ymin: bbox.minY,
      xmax: bbox.maxX,
      ymax: bbox.maxY,
      spatialReference: { wkid: 4326 },
    }),
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
  };
}

export interface LoadResult {
  features: GeoJSON.Feature[];
  /**
   * The service had more to give than it returned.
   *
   * Worth reporting rather than inferring from the count, because a service
   * enforces its OWN ceiling: ArcGIS commonly caps a query at 1,000 or 2,000
   * records whatever `resultRecordCount` asks for. A tool that only checked
   * "did I get as many as I asked for" would call a truncated 1,000 of 40,000
   * a complete answer and say nothing.
   */
  truncated: boolean;
}

/** Features only, for callers that do not act on truncation. */
export async function loadFeatures(
  layer: any,
  ctx: ToolContext,
  options: LoadOptions = {},
): Promise<GeoJSON.Feature[]> {
  return (await loadFeaturesDetailed(layer, ctx, options)).features;
}

export async function loadFeaturesDetailed(
  layer: any,
  ctx: ToolContext,
  options: LoadOptions = {},
): Promise<LoadResult> {
  const limit = options.limit ?? 1000;

  // Already in memory. The caller's own bounding-box test does the narrowing,
  // so there is nothing to gain by pre-filtering here.
  if (layer.localData) {
    const all: GeoJSON.Feature[] = layer.localData.features ?? [];
    return { features: all.slice(0, limit), truncated: all.length > limit };
  }

  if (layer.arcgisLayerId !== undefined) {
    const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return { features: [], truncated: false };
    const params = new URLSearchParams({
      where: layer.filter?.trim() || '1=1',
      outFields: '*',
      outSR: '4326',
      f: 'geojson',
      resultRecordCount: String(limit),
      ...(options.bbox ? arcgisEnvelope(options.bbox) : {}),
    });
    const response = await fetch(
      withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
    );
    if (!response.ok) return { features: [], truncated: false };
    const data = await response.json();
    const features: GeoJSON.Feature[] = data?.features ?? [];
    // ArcGIS says so outright when it has clipped the answer.
    const exceeded =
      data?.exceededTransferLimit === true || data?.properties?.exceededTransferLimit === true;
    return { features, truncated: exceeded || features.length >= limit };
  }

  if (!ctx.geoServerUrl) return { features: [], truncated: false };
  try {
    const url = new URL(ctx.geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('typeNames', layer.name);
    url.searchParams.set('outputFormat', 'application/json');
    // Without this, a layer whose STORED (native) CRS is not WGS84 comes back
    // in that native CRS — every spatial-relate caller of this (Select By
    // Location, the print legend's extent filter) assumes EPSG:4326 lon/lat,
    // so such a layer's features silently never intersect anything. See
    // featureSearchService.ts, the one other WFS GetFeature caller that
    // already set this. Unrelated to the axis-order note just below: that is
    // about a BBOX parameter's own axis order, and this function sends none.
    url.searchParams.set('srsName', 'EPSG:4326');
    url.searchParams.set('count', String(limit));
    /* No bbox here, deliberately. WFS 2.0 flipped the axis order of geographic
       CRSs, and servers disagree about it in practice — a bbox sent the wrong
       way round returns the wrong features silently, which is far worse than
       fetching more than we needed and narrowing them here. */
    const response = await fetch(url.toString());
    if (!response.ok) return { features: [], truncated: false };
    const data = await response.json();
    const features: GeoJSON.Feature[] = data?.features ?? [];
    /* WFS 2.0 reports both totals. `numberMatched` is allowed to be the string
       "unknown", so it is only trusted when it is genuinely a number. */
    const matched = data?.numberMatched;
    const returned = data?.numberReturned;
    const known = typeof matched === 'number' && typeof returned === 'number';
    return {
      features,
      truncated: known ? matched > returned : features.length >= limit,
    };
  } catch {
    return { features: [], truncated: false };
  }
}

/** Representative point for any geometry — the mean of its coordinates. */
export function centroidOf(geometry: GeoJSON.Geometry | null | undefined): [number, number] | null {
  if (!geometry) return null;
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  const walk = (value: any) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      sumX += value[0];
      sumY += value[1];
      count += 1;
      return;
    }
    for (const part of value) walk(part);
  };
  walk((geometry as any).coordinates);
  return count === 0 ? null : [sumX / count, sumY / count];
}

/**
 * The first readable label on a feature, for a result row.
 *
 * Takes the two members it actually reads rather than a whole Feature, so a
 * selection row — which carries the same attributes but no geometry — can be
 * labelled without being rebuilt into a fake Feature first.
 */
export function labelOf(
  feature: { properties?: Record<string, unknown> | null; id?: string | number },
  index: number,
): string {
  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  const preferred = Object.keys(properties).find((key) =>
    /^(name|label|title|address|description)$/i.test(key),
  );
  const value = preferred ? properties[preferred] : undefined;
  if (value !== undefined && value !== null && String(value).trim() !== '') return String(value);
  const id = properties.OBJECTID ?? properties.objectid ?? properties.fid ?? feature.id;
  return id !== undefined ? `Feature ${id}` : `Feature ${index + 1}`;
}
